import * as React from 'react';

import { cn } from '../../lib/utils';
import { PackageBadge, type PackageBadgeType } from '../package-badge';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '../ui/card';

/**
 * PackageInfoPanel — kompakt csomag-összefoglaló panel (E-105 Phase 2 Story
 * S3 — ADR-117 + ADR-118). Megjeleníti a csomag kulcs-adatait: típus-badge,
 * név, packageCode, scope, érvényesség, árazás (fix / napi), kaució, tétel-
 * szám, leírás (ha van).
 *
 * A komponens **presentational** — nincs belső state, nincs data-fetching.
 * A RBAC-gate-et (admin override button megjelenés) a parent dönti el és az
 * `actionSlot` prop-on keresztül adja át.
 *
 * Drift + expired jelzések `<PackageBadge>`-en jelennek meg (ADR-117 D-4 + D-5
 * integrációs pont a worksheet / rental UI-hoz).
 *
 * i18n: a címkék ma magyar fix string-ek (rövid épülési idő miatt). Az
 * `@kgc/i18n` integrációt egy későbbi story hozza be (külön PR, WizardPanel
 * mintájához illeszkedő time-box).
 */

export interface PackageInfoPanelPackage {
  id: string;
  packageCode: string;
  name: string;
  description?: string | null;
  type: PackageBadgeType;
  scope: 'CENTRAL' | 'LOCAL';
  fixedPrice?: number | null;
  dailyRate?: number | null;
  depositAmount?: number | null;
  validFrom?: Date | string | null;
  validTo?: Date | string | null;
  itemCount?: number;
}

export interface PackageInfoPanelProps extends React.HTMLAttributes<HTMLDivElement> {
  package: PackageInfoPanelPackage;
  /** Csomag módosult a rögzítés óta (ADR-117 D-4). Badge-re propagál. */
  drift?: boolean;
  /** Lejárt `validTo` (ADR-117 D-5). Badge-re propagál. */
  expired?: boolean;
  /**
   * Opcionális akció-slot a CardFooter-ben — tipikusan `<PackageOverrideButton>`
   * admin user esetén. Undefined → footer nem renderelődik.
   */
  actionSlot?: React.ReactNode;
}

function formatHuf(amount: number): string {
  return `${amount.toLocaleString('hu-HU')} Ft`;
}

function formatDate(d: Date | string): string {
  const date = typeof d === 'string' ? new Date(d) : d;
  return date.toLocaleDateString('hu-HU', { year: 'numeric', month: '2-digit', day: '2-digit' });
}

function scopeLabel(scope: 'CENTRAL' | 'LOCAL'): string {
  return scope === 'CENTRAL' ? 'Központi' : 'Helyi';
}

export function PackageInfoPanel({
  package: pkg,
  drift = false,
  expired = false,
  actionSlot,
  className,
  ...divProps
}: PackageInfoPanelProps) {
  const hasValidity = pkg.validFrom != null || pkg.validTo != null;
  const validityText = (() => {
    if (!hasValidity) return null;
    const from = pkg.validFrom != null ? formatDate(pkg.validFrom) : '∞';
    const to = pkg.validTo != null ? formatDate(pkg.validTo) : '∞';
    return `${from} → ${to}`;
  })();

  return (
    <Card className={cn('w-full', className)} data-testid="package-info-panel" {...divProps}>
      <CardHeader className="space-y-2 pb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <PackageBadge packageType={pkg.type} drift={drift} expired={expired} size="sm" />
          <span className="text-xs text-muted-foreground font-mono">{pkg.packageCode}</span>
          <span className="text-xs text-muted-foreground">·</span>
          <span className="text-xs text-muted-foreground">{scopeLabel(pkg.scope)}</span>
        </div>
        <CardTitle className="text-base">{pkg.name}</CardTitle>
        {validityText != null && (
          <div className="text-xs text-muted-foreground">
            <span className="font-medium">Érvényesség:</span> {validityText}
          </div>
        )}
      </CardHeader>

      <CardContent className="space-y-2 pb-3 text-sm">
        {pkg.description != null && pkg.description.length > 0 && (
          <p className="text-muted-foreground">{pkg.description}</p>
        )}

        <dl className="grid grid-cols-2 gap-x-4 gap-y-1">
          {pkg.fixedPrice != null && (
            <div>
              <dt className="text-xs text-muted-foreground">Fix ár</dt>
              <dd className="font-medium">{formatHuf(pkg.fixedPrice)}</dd>
            </div>
          )}
          {pkg.dailyRate != null && (
            <div>
              <dt className="text-xs text-muted-foreground">Napidíj</dt>
              <dd className="font-medium">{formatHuf(pkg.dailyRate)}</dd>
            </div>
          )}
          {pkg.depositAmount != null && (
            <div>
              <dt className="text-xs text-muted-foreground">Kaució</dt>
              <dd className="font-medium">{formatHuf(pkg.depositAmount)}</dd>
            </div>
          )}
          {pkg.itemCount != null && (
            <div>
              <dt className="text-xs text-muted-foreground">Tételek</dt>
              <dd className="font-medium">{pkg.itemCount} db</dd>
            </div>
          )}
        </dl>
      </CardContent>

      {actionSlot != null && (
        <CardFooter className="pt-0" data-testid="package-info-panel-footer">
          {actionSlot}
        </CardFooter>
      )}
    </Card>
  );
}
